/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable no-debugger */
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { FiltersComponent } from '@/app/shared/filters/filters.component';
import { SearchBarComponent } from '@/app/shared/search-bar/search-bar.component';
import { TabsComponent } from '@/app/shared/tabs/tabs.component';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, signal, computed, effect, NgZone, OnInit, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { filtersAdvanced } from '@/app/interfaces/filtersAdvanced';
import { ProjectCardComponent } from '@/app/shared/cards/project-card/project-card.component';
import { DropdownComponent } from '@/app/shared/dropdown/dropdown.component';
import { filtersBasic } from '@/app/interfaces/filtersBasic';
import { ProjectsResponse } from '@/app/interfaces/projects';
import { ProjectsService } from '@/app/services/ia-services/projects.service';
import { SpdfService } from '@/app/services/ia-services/spdf.service';
import { DesyAngularModule } from 'desy-angular';
import { PaginationComponent } from '@/app/shared/pagination/pagination.component';
import { Button, Project } from '@/app/interfaces/project';
import { SpdfGenerationRequest, SpdfReportContext } from '@/app/interfaces/report-spdf';
import { FiltersService } from '@/app/services/filters.service';
import { ToastService } from '@/app/services/script/toast.service';
import { lastValueFrom } from 'rxjs';
import { NormalizeDatePipe } from '@/app/pipe/normalizeDate.pipe';
import { environment } from '@/environments/environment';
import { getProjectStateFilterOptions } from '@/app/shared/mappers/project-state.mapper';
import {
  DateRangeFilterValue,
  DynamicFilterItem,
  DynamicFiltersResponse,
  ProjectsAdvancedFilter,
  ProjectsListAdvancedParams,
  ProjectsListParams,
  StoredUserData,
  TabId,
  WorkflowFilter,
  WorkflowFrontendRole,
  workflowRoleMap,
} from '@/app/interfaces/projects-list';

@Component({
  selector: 'app-projects-list',
  imports: [
    CommonModule,
    DesyAngularModule,
    FormsModule,
    TabsComponent,
    ButtonsComponent,
    SearchBarComponent,
    FiltersComponent,
    ProjectCardComponent,
    PaginationComponent,
    DropdownComponent,
  ],
  templateUrl: './projects-list.component.html',
  styleUrl: './projects-list.component.scss',
  providers: [NormalizeDatePipe],
})
export class ProjectsListComponent implements OnInit {
  readonly defaultCardDate = new Date();
  readonly noCreateProjectPermissionMessage = 'No tienes permisos para crear proyectos';
  private readonly userData: StoredUserData = this.getStoredUserData();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private projectsService: ProjectsService,
    private spdfService: SpdfService,
    private filtersService: FiltersService,
    private normalizeDate: NormalizeDatePipe,
    private ngZone: NgZone,
    private toastService: ToastService
  ) {
    effect(() => {
      this.cards.set([]);
      this.loadProjects();
    });
  }

  selectedTab = signal<TabId>('myProjects');
  workflowFilter = computed(() => this.getWorkflowFilter());
  tabOptions = computed(() => {
    const tabs: { title: string; url: string; id: TabId }[] = [{ title: 'Mis proyectos', url: '', id: 'myProjects' }];

    if (this.workflowFilter()) {
      tabs.push({ title: 'Para revisión', url: '', id: 'reviewProjects' });
    }

    return tabs;
  });

  paginationSize = signal(10);
  currentPage = signal(1);
  isLoading = signal(false);
  cards = signal<Project[]>([]);
  total = signal(0);

  userLogged = this.userData;
  search = signal('');
  filters = signal<DynamicFiltersResponse>({});

  orgCode = this.userData.orgCode ?? '';
  jobCode = this.userData.roleCode ?? '';

  organisms = [
    { label: 'Ministerio de Salud Pública', value: 'ORG46546', check: false },
    { label: 'Secretaría de Energía y Ambiente', value: 'ORG39218', check: false },
    { label: 'Agencia Nacional de Tecnología', value: 'ORG28495', check: false },
    { label: 'Dirección General de Transporte', value: 'ORG83920', check: false },
    { label: 'Instituto de Estadísticas y Censos', value: 'ORG58172', check: false },
    { label: 'Consejo Federal de Educación', value: 'ORG17460', check: false },
    { label: 'Administración Nacional de Seguridad Social', value: 'ORG91435', check: false },
    { label: 'Superintendencia de Riesgos del Trabajo', value: 'ORG78231', check: false },
    { label: 'Agencia de Protección Ambiental', value: 'ORG64219', check: false },
    { label: 'Ministerio de Innovación y Ciencia', value: 'ORG34786', check: false },
    { label: 'Secretaría de Agricultura Familiar', value: 'ORG91822', check: false },
    { label: 'Instituto Nacional de Cine y Artes Audiovisuales', value: 'ORG46387', check: false },
    { label: 'Dirección de Migraciones', value: 'ORG23488', check: false },
    { label: 'Comisión Nacional de Valores', value: 'ORG76452', check: false },
    { label: 'Agencia de Acceso a la Información Pública', value: 'ORG50913', check: false },
  ];

  baseFilters: filtersAdvanced[] = [
    {
      type: 'multiselect',
      label: 'Estado',
      options: getProjectStateFilterOptions(),
      selected: [],
    },
    {
      type: 'dateRange',
      label: 'Fecha de creación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
      selected: null,
    },
    {
      type: 'dateRange',
      label: 'Fecha de modificación',
      dateRangeOptions: {
        startDate: '',
        endDate: '',
      },
      selected: null,
    },
  ];
  advancedFilters = signal<filtersAdvanced[]>([...this.baseFilters]);
  visibleAdvancedFilters = computed(() => {
    if (this.selectedTab() === 'reviewProjects') {
      return this.advancedFilters().filter((filter) => filter.label !== 'Estado');
    }

    return this.advancedFilters();
  });
  orderby: filtersBasic = {
    type: 'dropdown',
    label: 'Ordenar proyectos de IA',
    classes: 'text-sm py-1',
    icon: 'pi pi-sort-alt',
    options: [
      { label: 'Ordenar por más reciente', value: 'recent' },
      { label: 'Ordenar por menos reciente', value: 'oldest' },
      { label: 'Ordenar por última modificación', value: 'update' },
    ],
    selected: '',
  };
  orderByName = signal('fecha_registro');
  orderDirection = signal('asc');
  org = signal<string[]>([]);
  state = signal<string[]>([]);
  creationDate = signal<DateRangeFilterValue>({});
  modificationDate = signal<DateRangeFilterValue>({});
  procedure = signal<string[]>([]);
  application = signal<string[]>([]);
  system = signal<string[]>([]);

  get params(): [ProjectsListParams, ProjectsListAdvancedParams] {
    const advancedFilter: ProjectsAdvancedFilter = {
      org: this.org(),
      state: this.state(),
      creationDate: this.creationDate(),
      modificationDate: this.modificationDate(),
      procedure: this.procedure(),
      application: this.application(),
      system: this.system(),
      search: this.search(),
    };

    const jobcode = this.getUserJobCode();
    const serviceManagerOrg = this.userData.roles?.jefe_servicio;
    const workflow = this.workflowFilter();

    if (this.selectedTab() === 'myProjects' && jobcode && !this.isGovernanceOfficeUser()) {
      advancedFilter.jobcode = jobcode;
    }

    if (this.selectedTab() === 'myProjects' && Array.isArray(serviceManagerOrg) && serviceManagerOrg.length > 0) {
      advancedFilter.serviceManagerOrg = serviceManagerOrg.map((orgCode) => this.normalizeWorkflowOrgCode(orgCode));
    }

    if (this.selectedTab() === 'reviewProjects' && workflow) {
      advancedFilter.workflow = workflow;
    }

    return [
      {
        basicFilter: {
          currentPage: this.currentPage(),
          sizePages: this.paginationSize(),
          nameOrderBy: this.orderByName(),
          orderBy: this.orderDirection(),
        },
      },
      {
        advancedFilter,
      },
    ];
  }

  ngOnInit() {
    localStorage.removeItem('selectedFilters');

    this.state.set([]);

    this.getFilters();
  }

  get canCreateProject(): boolean {
    const roles = this.userLogged?.roles as Record<string, string[]> | undefined;
    const currentOrgCode = this.userLogged?.orgCode;
    const currentOrgCandidates = currentOrgCode ? [currentOrgCode, `${environment.environment}${currentOrgCode}`] : [];
    const hasPermission = !!roles?.jefe_consumidor?.some((orgCode) => currentOrgCandidates.includes(orgCode));

    if (!hasPermission) {
      return false;
    }

    return hasPermission;
  }

  handleSearch(value: string) {
    this.search.set(value);
  }

  async getFilters() {
    const { data } = await lastValueFrom(
      await this.filtersService.getFilters({
        filter: 'proyecto',
        params: {
          ORGCODE: this.orgCode,
          JOBCODE: this.jobCode,
        },
      })
    );

    this.filters.set(data as DynamicFiltersResponse);
    this.setDynamicFilters(data as DynamicFiltersResponse);
  }

  private getStoredUserData(): StoredUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return JSON.parse(storedUserData) as StoredUserData;
  }

  private getUserJobCode(): string | undefined {
    const jobcode = this.userData.roleCode?.trim();

    return jobcode ? jobcode : undefined;
  }

  private getWorkflowFilter(): WorkflowFilter | undefined {
    const roles = this.userData.roles ?? {};

    for (const [role, org] of Object.entries(roles)) {
      if (this.isGovernanceOfficeRole(role) && Array.isArray(org) && org.length > 0) {
        return {
          role: workflowRoleMap[role],
          org: org.map((orgCode) => this.normalizeWorkflowOrgCode(orgCode)),
        };
      }
    }

    return undefined;
  }

  private isGovernanceOfficeUser(): boolean {
    return Object.keys(this.userData.roles ?? {}).some((role) => this.isGovernanceOfficeRole(role));
  }

  private isGovernanceOfficeRole(role: string): role is WorkflowFrontendRole {
    return role in workflowRoleMap;
  }

  private normalizeWorkflowOrgCode(orgCode: string): string {
    const normalizedOrgCode = orgCode.match(/ORG\d+$/);

    return normalizedOrgCode ? normalizedOrgCode[0] : orgCode;
  }

  private isDateRangeFilterValue(value: unknown): value is DateRangeFilterValue {
    return typeof value === 'object' && value !== null && ('startDate' in value || 'endDate' in value);
  }

  private getSelectedStringValues(selected?: string | string[] | number | number[]): string[] {
    return Array.isArray(selected) ? selected.filter((value): value is string => typeof value === 'string') : [];
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private buildReviewersState(card: Project): string {
    const states = [
      card.technicalReviewerState,
      card.legalReviewerState,
      card.functionalReviewerState,
      card.dpdReviewerState,
    ].map((reviewState) => this.normalizeReviewState(reviewState));
    const allReviewed = states.every((reviewState) => reviewState !== 'PENDING');
    return allReviewed ? 'APPROVED' : 'PENDING';
  }

  private buildStateGroup(card: Project): { group: string; state: string }[] {
    return [
      { group: 'support', state: this.normalizeReviewState(card.supportGroupState) },
      { group: 'reviewers', state: this.buildReviewersState(card) },
      { group: 'chief', state: this.normalizeReviewState(card.serviceManagerState) },
    ];
  }

  private getUserRolesForProject(card: Project): string[] {
    const projectOrgs = [card.codigo_organismo, card.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => environment.environment + org);

    return Object.entries(this.userData.roles ?? {})
      .filter(([, orgs]: [string, unknown]) => {
        return Array.isArray(orgs) && orgs.some((org) => projectOrgs.includes(org));
      })
      .map(([roleKey]) => roleKey as string);
  }

  private canCurrentUserReview(card: Project): boolean {
    const userRoles = this.getUserRolesForProject(card);

    const stateGroup = this.buildStateGroup(card);
    const activeStage = stateGroup.find((stage) => stage.state === 'PENDING');

    if (!activeStage) return false;

    switch (activeStage.group) {
      case 'support':
        return userRoles.includes('apoyo_OGIA');

      case 'reviewers': {
        const roleStateMap: Record<string, string | undefined> = {
          funcional_OGIA: card.functionalReviewerState,
          juridico_OGIA: card.legalReviewerState,
          tecnico_OGIA: card.technicalReviewerState,
          dpd_OGIA: card.dpdReviewerState,
        };

        return userRoles
          .filter((role) => role in roleStateMap)
          .some((role) => this.normalizeReviewState(roleStateMap[role]) === 'PENDING');
      }

      case 'chief':
        return userRoles.includes('jefe_servicio_OGIA');

      default:
        return false;
    }
  }

  private canCurrentUserViewReview(card: Project): boolean {
    if ((card.state ?? '').toUpperCase() === 'CORRECTION') {
      return this.isCurrentUserReviewRegistered(card);
    }

    if (this.normalizeReviewState(card.serviceManagerState) !== 'PENDING') {
      return false;
    }

    return this.isCurrentUserReviewRegistered(card);
  }

  private isCurrentUserReviewRegistered(card: Project): boolean {
    const userRoles = this.getUserRolesForProject(card);
    const roleStateMap: Record<string, string | undefined> = {
      apoyo_OGIA: card.supportGroupState,
      funcional_OGIA: card.functionalReviewerState,
      juridico_OGIA: card.legalReviewerState,
      tecnico_OGIA: card.technicalReviewerState,
      dpd_OGIA: card.dpdReviewerState,
      jefe_servicio_OGIA: card.serviceManagerState,
    };

    return userRoles
      .filter((role) => role in roleStateMap)
      .some((role) => {
        const state = this.normalizeReviewState(roleStateMap[role]);
        return state === 'APPROVED' || state === 'CORRECTION';
      });
  }

  private getReviewButtonLabel(canReview: boolean): string {
    if (!canReview) {
      return 'Consultar revisión';
    }

    return 'Revisar proyecto';
  }

  private getProjectClassification(card: Project): string {
    const infoCardClassification = card.infoCard?.find((info) => info.label === 'Clasificación del sistema')?.value;
    // En el listado llega en infoCard; otras vistas comparten el tipo y lo reciben en projectClassification.
    const classification = card.projectClassification ?? infoCardClassification;

    return (Array.isArray(classification) ? classification[0] : classification) ?? '';
  }

  private hasUnacceptableRiskLevel(card: Project): boolean {
    return this.getProjectClassification(card) === 'inaceptable';
  }

  private canCurrentUserCorrect(card: Project): boolean {
    const projectState = (card.state ?? '').toUpperCase();
    if (projectState !== 'CORRECTION') return false;

    const userRoles = this.getUserRolesForProject(card);
    if (userRoles.includes('jefe_servicio_OGIA')) return false;

    const allowedRoles = ['responsable_IA', 'jefe_consumidor'];

    return userRoles.some((role) => allowedRoles.includes(role));
  }

  private buildCardButtons(card: Project) {
    const safeTitle = card.projectName?.replace(/ /g, '_');
    const isApproved = ['APPROVED', 'DEVELOPMENT', 'DEVELOPED'].includes((card.state ?? '').toUpperCase());
    const projectOrgCode = card.codigo_organismo;
    const canGenerateReport =
      (card.state ?? '').toUpperCase() === 'DEVELOPED_AND_REVIEWED' &&
      !!projectOrgCode &&
      (this.userData.roles?.jefe_servicio_OGIA?.some(
        (userOrg) => this.normalizeWorkflowOrgCode(userOrg) === this.normalizeWorkflowOrgCode(projectOrgCode)
      ) ?? false);
    const hasUnacceptableRiskLevel = this.hasUnacceptableRiskLevel(card);
    const buttons: Button[] = [];

    if (isApproved) {
      buttons.push({
        label: 'Registrar sistema de IA',
        disabled: hasUnacceptableRiskLevel,
        title: hasUnacceptableRiskLevel ? 'Riesgo inaceptable, no es posible registrar sistema' : undefined,
        url: `catalogo-ia/sistemas-de-ia/nuevo-sistema/por-proyecto/${card.id}/${safeTitle}/${card.code}/${card.codigo_organismo}`,
      });
    }

    buttons.push({
      label: 'Consultar detalle',
      disabled: false,
      url: 'catalogo-ia/proyectos/detalles/' + card.id,
    });

    if (canGenerateReport) {
      buttons.push({
        label: 'Generar informe',
        disabled: false,
        action: () => this.generateReport(card),
      });
    }

    if (this.canCurrentUserCorrect(card)) {
      buttons.push({
        label: 'Subsanar proyecto',
        disabled: false,
        url: `catalogo-ia/proyectos/detalles/${card.id}/subsanar`,
      });
    }

    const canReview = this.canCurrentUserReview(card);
    const canViewReview = this.canCurrentUserViewReview(card);

    if (canReview || canViewReview) {
      buttons.push({
        label: this.getReviewButtonLabel(canReview),
        disabled: false,
        url: `catalogo-ia/proyectos/detalles/${card.id}/revision`,
      });
    }
    return buttons;
  }

  async generateReport(card: Project): Promise<void> {
    const baseReportContext = this.buildSpdfReportContext(card);

    if (!baseReportContext) {
      this.toastService.showError('Error al generar el informe', 'No se pudo generar el informe para este proyecto.');
      return;
    }

    const projectId = String(card.id ?? '');

    try {
      const presignedReportUrl = await this.projectsService.generatePresignedReportUrl(baseReportContext.idProyecto);
      const reportDataResponse = await this.projectsService.getReportData({
        codigo_proyecto: projectId,
      });
      const reportData = reportDataResponse.data;
      const payload: SpdfGenerationRequest = {
        applicationId: reportData.applicationId,
        groupId: reportData.groupId,
        templateId: reportData.templateId,
        data: reportData.data,
        editorOptions: {
          allowGoBack: false,
          backUrl: `${this.document.location.origin}/catalogo-ia/visor-informe`,
        },
      };
      const session = await this.spdfService.createGenerationSession(reportData.applicationId, payload);
      const reportContext: SpdfReportContext = {
        ...baseReportContext,
        uploadUrl: presignedReportUrl.uploadUrl,
        s3Key: presignedReportUrl.s3Key,
      };

      sessionStorage.setItem(this.getSpdfReportContextStorageKey(session.generationId), JSON.stringify(reportContext));

      this.document.location.href = session.redirectUrl;
    } catch {
      return;
    }
  }

  private buildSpdfReportContext(
    card: Project
  ): Pick<SpdfReportContext, 'idProyecto' | 'nombreProyecto' | 'codigo_organismo'> | null {
    if (typeof card.id !== 'number' || !Number.isFinite(card.id)) {
      return null;
    }

    if (typeof card.projectName !== 'string' || !card.projectName.trim()) {
      return null;
    }

    if (typeof card.codigo_organismo !== 'string' || !card.codigo_organismo.trim()) {
      return null;
    }

    return {
      idProyecto: card.id,
      nombreProyecto: card.projectName,
      codigo_organismo: card.codigo_organismo,
    };
  }

  private getSpdfReportContextStorageKey(generationId: string): string {
    return `spdf-report-context:${generationId}`;
  }

  async loadProjects() {
    const paramsClear = this.params.map((param) => {
      if ('advancedFilter' in param) {
        const filteredAdvanced = Object.entries(param.advancedFilter)
          .filter(([_, value]) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'string') return value.trim() !== '';
            if (this.isDateRangeFilterValue(value)) return !!value.startDate || !!value.endDate;
            if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
            return value !== undefined && value !== null;
          })
          .reduce<Partial<ProjectsAdvancedFilter>>((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        return { ...param, advancedFilter: filteredAdvanced };
      }
      return param;
    });

    try {
      const projects: ProjectsResponse = await this.projectsService.getProjectsList({
        params: paramsClear,
      });
      this.ngZone.run(() => {
        this.cards.set(
          projects.data.items.map((card) => ({
            ...card,
            buttons: this.buildCardButtons(card),
          }))
        );

        this.total.set(projects.data.total);
      });
    } catch (err) {
      console.error('Error al cargar proyectos:', err);
      throw err;
    }
  }

  handleApplyFilters(filters: filtersAdvanced[]) {
    filters.forEach((filtro) => {
      switch (filtro.label) {
        case 'Estado':
          if (this.selectedTab() !== 'reviewProjects') {
            this.state.set(this.getSelectedStringValues(filtro.selected));
          }
          break;
        case 'Organismo':
          this.org.set(this.getSelectedStringValues(filtro.selected));
          break;
        case 'Procedimiento':
          this.procedure.set(this.getSelectedStringValues(filtro.selected));
          break;
        case 'Aplicación':
          this.application.set(this.getSelectedStringValues(filtro.selected));
          break;
        case 'Sistema':
          this.system.set(this.getSelectedStringValues(filtro.selected));
          break;
        case 'Fecha de creación':
          if (filtro.type === 'dateRange' && filtro.dateRangeOptions) {
            this.creationDate.set({
              startDate: this.normalizeDate.transform(filtro.dateRangeOptions.startDate),
              endDate: this.normalizeDate.transform(filtro.dateRangeOptions.endDate),
            });
          }
          break;
        case 'Fecha de modificación':
          if (filtro.type === 'dateRange' && filtro.dateRangeOptions) {
            this.modificationDate.set({
              startDate: this.normalizeDate.transform(filtro.dateRangeOptions.startDate),
              endDate: this.normalizeDate.transform(filtro.dateRangeOptions.endDate),
            });
          }
          break;
        default:
          break;
      }
    });
    localStorage.setItem('selectedFilters', JSON.stringify(filters));
  }

  onDropdownChange($event: string) {
    switch ($event) {
      case 'recent':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('desc');
        }
        break;
      case 'oldest':
        {
          this.orderByName.set('fecha_registro');
          this.orderDirection.set('asc');
        }
        break;
      case 'update':
        {
          this.orderByName.set('fecha_ultima_modificacion');
          this.orderDirection.set('desc');
        }
        break;
      default:
    }
  }

  setDynamicFilters(data: DynamicFiltersResponse) {
    const config: Record<
      keyof DynamicFiltersResponse,
      { label: string; mapFn: (item: DynamicFilterItem) => { label: string; value: string } }
    > = {
      organismo: {
        label: 'Organismo',
        mapFn: (item) => ({ label: item.name, value: item.name }),
      },
      procedimiento: {
        label: 'Procedimiento',
        mapFn: (item) => ({ label: item.name, value: item.name }),
      },
      aplicacion: {
        label: 'Aplicación',
        mapFn: (item) => ({ label: item.name, value: item.name }),
      },
      sistema: {
        label: 'Sistema',
        mapFn: (item) => ({ label: item.name, value: item.name }),
      },
    };

    const dynamicFilters: filtersAdvanced[] = (Object.keys(config) as (keyof DynamicFiltersResponse)[])
      .filter((key) => data[key])
      .map((key) => {
        const { label, mapFn } = config[key];
        const items = data[key] ?? [];
        return {
          type: 'multiselect',
          label,
          options: items.map(mapFn),
          selected: [],
        };
      });

    this.advancedFilters.set([...this.baseFilters, ...dynamicFilters]);
  }

  onTabChange(tabId: string) {
    if (tabId !== 'myProjects' && tabId !== 'reviewProjects') {
      return;
    }

    const nextTab: TabId = tabId;

    if (nextTab === 'reviewProjects' && !this.workflowFilter()) {
      return;
    }

    this.currentPage.set(1);
    this.selectedTab.set(nextTab);
    this.state.set([]);
  }
}
